import { Box } from '@mui/material';
import { useScalingValue } from 'hooks/useScalingValue';
import React, { ReactElement, ReactNode } from 'react';
import { FollowOrganizationButton } from 'components/app/FollowOrganizationButton';
import { useRouter } from 'next/router';
import { OrganizationSummary } from 'services/api';
import { Routes } from 'services/routes';
import { CalloutRow } from './CalloutRow';

export interface OrganizationsCalloutProps {
  action: 'none' | 'follow' | ReactNode;
  school?: {
    id: string;
    iconUrl: string;
    logoUrl: string;
    name: string;
    isFollowed: boolean;
    slug: string;
  };
  organizations: OrganizationSummary[];
  slug?: string;
  onOrganizationClick?: (organizationId: string, organizationName: string, organizationLogoUrl: string, schoolSlug?: string) => void;
}

export const OrganizationsCallout = ({
  action,
  school,
  organizations,
  slug,
  onOrganizationClick,
}: OrganizationsCalloutProps): ReactElement => {
  const router = useRouter();

  const numOrganizations = Math.round(useScalingValue(2, 5, true, 720, 920));

  if (organizations.length > numOrganizations) {
    const followed = organizations.filter(({ isFollowed }) => !!isFollowed);
    const unFollowed = organizations.filter(({ isFollowed }) => !isFollowed);

    organizations = [...unFollowed, ...followed];
  }

  const handleOrgClick = (organization: OrganizationSummary) => {
    if (onOrganizationClick) {
      onOrganizationClick(
        organization.id,
        organization.name,
        organization.logoUrl,
        school?.slug || slug,
      );
    } else {
      void router.push(
        `${Routes.explore(school?.slug || slug)}?organization=${organization.id}`,
      );
    }
  };

  const handleSchoolClick = (schoolSlug: string) => {
    void router.push(Routes.explore(schoolSlug));
  };

  return (
    <Box>
      {school && (
        <CalloutRow
          name={school.name}
          logoUrl={school.iconUrl}
          onClick={() => {
            handleSchoolClick(school.slug);
          }}
        />
      )}
      {organizations.slice(0, numOrganizations).map(organization => {
        return (
          <React.Fragment key={organization.id}>
            <CalloutRow
              action={
                action === 'follow' ? (
                  <FollowOrganizationButton organizationId={organization.id} />
                ) : action !== 'none' ? (
                  action
                ) : undefined
              }
              name={organization.name}
              logoUrl={organization.logoUrl}
              onClick={() => {
                void handleOrgClick(organization);
              }}
            />
          </React.Fragment>
        );
      })}
    </Box>
  );
};
